import { get } from 'lodash';

import { ASSET_TYPE, FIELD_PATHS, FIELD_VALUES, PROPERTY_EXPENSE_TYPE } from '@funnels/constants';
import { Asset, Liability, PropertyExpenses } from '@funnels/types';

import getAssetPurpose from './getAssetPurpose';

type PropertyExpensesIndexesFromAsset = {
  assetIndex: number;
  expensesIndexes: number[];
};

function getExpenseIndexByType(expenses: PropertyExpenses[], type: PROPERTY_EXPENSE_TYPE) {
  return expenses.findIndex((expense) => expense.type === type);
}

function getExpenseAmountByType(expenses: PropertyExpenses[], type: PROPERTY_EXPENSE_TYPE) {
  return expenses.find((expense) => expense.type === type)?.amount;
}

export default function getPropertyExpensesIndexesFromAssets(params: {
  assets: Asset[];
  liabilities: Liability[];
}): PropertyExpensesIndexesFromAsset[] {
  const { assets } = params;
  const propertyIndexes = assets.reduce((accumulator: number[], asset: Asset, index: number) => {
    const isOther = getAssetPurpose(params, index) === FIELD_VALUES.OTHER;
    const isToBeSold = get(params, FIELD_PATHS.ASSET_TO_BE_SOLD(index));
    if (isOther && isToBeSold) return accumulator;

    if (get({ assets: [asset] }, FIELD_PATHS.ASSET_TYPE(0)) === ASSET_TYPE.PROPERTY) {
      return [...accumulator, index];
    }
    return accumulator;
  }, []);
  const hasMoreThanOneOwnerOccupied =
    propertyIndexes.filter(
      (currentIndex) => get(params, FIELD_PATHS.ASSET_PRIMARY_PURPOSE(currentIndex)) === FIELD_VALUES.OWNER_OCCUPIED
    ).length > 1;

  return propertyIndexes.map((assetIndex) => {
    const expensesIndexes: number[] = [];
    const expenses: PropertyExpenses[] = get(params, FIELD_PATHS.ASSET_PROPERTY_EXPENSES(assetIndex)) || [];
    const primaryPurpose = get(params, FIELD_PATHS.ASSET_PRIMARY_PURPOSE(assetIndex));
    const isInvestment = primaryPurpose === FIELD_VALUES.INVESTMENT;

    if (isInvestment) {
      const investmentPropertyRunningCostsIndex = getExpenseIndexByType(
        expenses,
        PROPERTY_EXPENSE_TYPE.INVESTMENT_PROPERTY_RUNNING_COSTS
      );
      if (investmentPropertyRunningCostsIndex > -1) {
        expensesIndexes.push(investmentPropertyRunningCostsIndex);
      }
    } else {
      const bodyCorporateFeesStrataFeesAndLandTax = getExpenseIndexByType(
        expenses,
        PROPERTY_EXPENSE_TYPE.BODY_CORPORATE_FEES_STRATA_FEES_AND_LAND_TAX
      );

      let showBodyCorporateFeesStrataFeesAndLandTax = false;
      if (bodyCorporateFeesStrataFeesAndLandTax > -1) {
        const bodyCorporateFeesStrataFeesAndLandTaxAmount = getExpenseAmountByType(
          expenses,
          PROPERTY_EXPENSE_TYPE.BODY_CORPORATE_FEES_STRATA_FEES_AND_LAND_TAX
        );
        if (bodyCorporateFeesStrataFeesAndLandTaxAmount && bodyCorporateFeesStrataFeesAndLandTaxAmount > 0) {
          showBodyCorporateFeesStrataFeesAndLandTax = true;
          expensesIndexes.push(bodyCorporateFeesStrataFeesAndLandTax);
        }
      }

      const bodyCorporateFeesStrataFees = getExpenseIndexByType(
        expenses,
        PROPERTY_EXPENSE_TYPE.STRATA_BODY_CORPORATE_FEES
      );
      if (bodyCorporateFeesStrataFees > -1) {
        const bodyCorporateFeesStrataFeesAmount = getExpenseAmountByType(
          expenses,
          PROPERTY_EXPENSE_TYPE.STRATA_BODY_CORPORATE_FEES
        );

        if (
          !showBodyCorporateFeesStrataFeesAndLandTax ||
          (bodyCorporateFeesStrataFeesAmount && bodyCorporateFeesStrataFeesAmount > 0)
        ) {
          expensesIndexes.push(bodyCorporateFeesStrataFees);
        }
      }

      const landTax = getExpenseIndexByType(expenses, PROPERTY_EXPENSE_TYPE.LAND_TAX);
      if (landTax > -1) {
        const landTaxAmount = getExpenseAmountByType(expenses, PROPERTY_EXPENSE_TYPE.LAND_TAX);
        if (!showBodyCorporateFeesStrataFeesAndLandTax || (landTaxAmount && landTaxAmount > 0)) {
          expensesIndexes.push(landTax);
        }
      }

      const primaryResidenceRunningCosts = getExpenseIndexByType(
        expenses,
        PROPERTY_EXPENSE_TYPE.PRIMARY_RESIDENCE_RUNNING_COSTS
      );
      if (primaryResidenceRunningCosts > -1) {
        expensesIndexes.push(primaryResidenceRunningCosts);
      }
      if (hasMoreThanOneOwnerOccupied) {
        const secondaryResidenceRunningCosts = getExpenseIndexByType(
          expenses,
          PROPERTY_EXPENSE_TYPE.SECONDARY_RESIDENCE_RUNNING_COSTS
        );
        if (secondaryResidenceRunningCosts > -1) {
          expensesIndexes.push(secondaryResidenceRunningCosts);
        }
      }
    }

    return {
      assetIndex,
      expensesIndexes,
    };
  });
}
